<template>
  <span class="vf-o-posthog-tracking" aria-hidden="true" />
</template>
<script lang="ts" setup>
// Renderless PostHog identity, cart and checkout-entry tracking (contract C4).
// Everything starts in onMounted: on the server this renders an empty span and
// subscribes to nothing. Calls go through the SSR-safe productAnalytics facade,
// which queues them until the client loader decides whether PostHog runs.
import { onBeforeUnmount, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import {
  phIdentifyCustomer,
  phResetCustomer,
  phTrack,
} from "~/core/analytics/productAnalytics";
import {
  posthogCartChange,
  posthogCheckoutStarted,
  stashWithoutExpiredGuestPerson,
} from "~/core/analytics/posthogEvents";
import {
  cartLineToGa4Item,
  cartToGa4Ecommerce,
} from "~/core/utils/ga4Ecommerce";

const APPLY_SESSION = "common/customer/applySession";
const LOGOUT = "common/customer/logout";
const CART_ADD = "store/cart/add";
const CART_UPDATE = "store/cart/update";
const CART_REMOVE = "store/cart/remove";
const CHECKOUT_PATH = "/store/checkout";

const store = useStore();
const route = useRoute();

// ---------------------------------------------------------------------------
// Identity (owner decision D5): oc_<customer_id> with the account email as a
// person property. reset() only on an explicit logout or a customer switch;
// a session that expires or a failed session check (clearPrivate) keeps it.
// ---------------------------------------------------------------------------
let identifiedId = "";
let identifiedEmail = "";
let loggingOut = false;

const customerId = (customer: any): string => {
  const id = String(customer?.id ?? "").trim();
  return /^[1-9]\d*$/.test(id) ? id : "";
};

const identifyCurrentCustomer = () => {
  if (!store.getters["common/customer/auth"]) return;
  const customer = store.getters["common/customer/get"];
  const id = customerId(customer);
  if (!id) return;
  const email =
    typeof customer?.email === "string" ? customer.email.trim() : "";
  if (id === identifiedId && email === identifiedEmail) return;
  // Another customer signed in on this page: forget the previous one first.
  if (identifiedId && identifiedId !== id) phResetCustomer();
  identifiedId = id;
  identifiedEmail = email;
  phIdentifyCustomer({ id, email: email || null });
};

const finishLogout = () => {
  if (!loggingOut) return;
  loggingOut = false;
  identifiedId = "";
  identifiedEmail = "";
  phResetCustomer();
};

// ---------------------------------------------------------------------------
// Product Added / Product Removed from the Vuex cart diff, so every add path
// (PDP, cards, wishlist, bag restore) and every quantity change is covered
// once. The GA4 add_to_cart/remove_from_cart sinks send nothing to PostHog.
// ---------------------------------------------------------------------------
type CartEntry = { quantity: number; line: any };
type PendingCartAction = {
  ids: string[];
  before: Map<string, CartEntry>;
  restored: boolean;
  adding: boolean;
};

const cartLines = (): any[] => {
  const products = store.getters["store/cart/get"]?.products;
  return Array.isArray(products) ? products : [];
};

const cartSnapshot = (): Map<string, CartEntry> => {
  const snapshot = new Map<string, CartEntry>();
  for (const line of cartLines()) {
    const id = String(line?.product?.id ?? "");
    if (!id) continue;
    const quantity = Number(line?.quantity) || 0;
    const entry = snapshot.get(id);
    if (entry) entry.quantity += quantity;
    else snapshot.set(id, { quantity, line });
  }
  return snapshot;
};

const productIdForKey = (key: unknown): string => {
  const line = cartLines().find(
    (item: any) => String(item?.key) === String(key)
  );
  return line ? String(line.product?.id ?? "") : "";
};

// add() raises an existing line through update(); that inner update belongs
// to the add, which reports it.
const addsInFlight = new Map<string, number>();
const pendingCartActions = new WeakMap<object, PendingCartAction>();

const beforeCartAction = (action: any) => {
  if (action.type === CART_ADD) {
    const items = action.payload?.product;
    const ids = (Array.isArray(items) ? items : [items])
      .map((product: any) => String(product?.id ?? ""))
      .filter(Boolean);
    if (!ids.length) return;
    ids.forEach((id) => addsInFlight.set(id, (addsInFlight.get(id) || 0) + 1));
    pendingCartActions.set(action, {
      ids,
      before: cartSnapshot(),
      // A bag rebuilt from an email link: the lines were added on another
      // device first, so these adds are marked.
      restored: action.payload?.restore === true,
      adding: true,
    });
  } else if (action.type === CART_UPDATE || action.type === CART_REMOVE) {
    const id = productIdForKey(action.payload?.key);
    if (!id) return;
    if (action.type === CART_UPDATE && addsInFlight.get(id)) return;
    pendingCartActions.set(action, {
      ids: [id],
      before: cartSnapshot(),
      restored: false,
      adding: false,
    });
  }
};

const afterCartAction = (action: any, failed: boolean) => {
  const pending = pendingCartActions.get(action);
  if (!pending) return;
  pendingCartActions.delete(action);
  if (pending.adding) {
    pending.ids.forEach((id) => {
      const left = (addsInFlight.get(id) || 1) - 1;
      if (left > 0) addsInFlight.set(id, left);
      else addsInFlight.delete(id);
    });
  }
  if (failed) return;
  const after = cartSnapshot();
  for (const id of new Set(pending.ids)) {
    const previous = pending.before.get(id);
    const current = after.get(id);
    const delta = (current?.quantity || 0) - (previous?.quantity || 0);
    if (!delta) continue;
    if (delta > 0 && action.type === CART_REMOVE) continue;
    if (delta < 0 && action.type === CART_ADD) continue;
    const change = posthogCartChange(
      delta > 0 ? "added" : "removed",
      cartLineToGa4Item(current?.line || previous?.line),
      Math.abs(delta),
      pending.restored ? { restored: true } : undefined
    );
    if (change) phTrack(change.event, change.props);
  }
};

// ---------------------------------------------------------------------------
// Checkout Started, standard path: one per visit to /store/checkout (query
// changes on the page are the same visit). It waits for a cart with lines.
// The drawer's GA4 begin_checkout is not mirrored; the express wallet on the
// product page sends its own start (express-checkout.vue).
// ---------------------------------------------------------------------------
let onCheckoutPage = false;
let checkoutStartPending = false;

const isCheckoutPath = () => route.path.replace(/\/+$/, "") === CHECKOUT_PATH;

const trackCheckoutStarted = () => {
  if (!checkoutStartPending || !isCheckoutPath()) return;
  const ecommerce = cartToGa4Ecommerce(store.getters["store/cart/get"]);
  if (!ecommerce.items.length) return;
  checkoutStartPending = false;
  const started = posthogCheckoutStarted(ecommerce, "standard");
  phTrack(started.event, started.props);
};

const routeChanged = () => {
  const checkout = isCheckoutPath();
  if (checkout && !onCheckoutPage) checkoutStartPending = true;
  if (!checkout) checkoutStartPending = false;
  onCheckoutPage = checkout;
  trackCheckoutStarted();
};

// ---------------------------------------------------------------------------
// Consent: a later analytics grant starts PostHog on this page; tell it who
// is signed in. A withdrawal reloads the page (consent.ts), nothing to do.
// ---------------------------------------------------------------------------
let analyticsAllowed = false;
const consentChanged = (event: Event) => {
  const granted = (event as CustomEvent)?.detail?.analytics === true;
  const newlyGranted = granted && !analyticsAllowed;
  analyticsAllowed = granted;
  if (!newlyGranted) return;
  identifiedId = "";
  identifiedEmail = "";
  identifyCurrentCustomer();
};

// ---------------------------------------------------------------------------
// Guest email lifetime (owner decision D5b): the order email waits in the
// vf_pending_purchase stash only for the success page, which removes the
// stash. A stash left by an abandoned payment is past the success page's
// 2 h expiry on a later visit; its email is removed then, with or without
// analytics consent, instead of waiting for the next checkout.
// ---------------------------------------------------------------------------
const PENDING_PURCHASE_KEY = "vf_pending_purchase";

const dropExpiredGuestEmail = () => {
  try {
    const storage = window.localStorage;
    const next = stashWithoutExpiredGuestPerson(
      storage.getItem(PENDING_PURCHASE_KEY),
      Date.now()
    );
    if (next !== null) storage.setItem(PENDING_PURCHASE_KEY, next);
  } catch {
    // Storage can be unavailable in hardened privacy modes.
  }
};

const stops: Array<() => void> = [];

onMounted(() => {
  dropExpiredGuestEmail();
  analyticsAllowed = window.vfConsent?.has("analytics") === true;
  stops.push(
    store.subscribe((mutation: any) => {
      if (mutation.type !== APPLY_SESSION) return;
      // Vuex does not guard mutation subscribers: never break a commit.
      try {
        identifyCurrentCustomer();
      } catch {
        // Product analytics must never interrupt the storefront.
      }
    }),
    store.subscribeAction({
      before: (action: any) => {
        if (action.type === LOGOUT) {
          loggingOut = Boolean(
            store.getters["common/customer/auth"] || identifiedId
          );
          // Sent while still identified, so it belongs to the customer.
          if (loggingOut) phTrack("Logged Out");
          return;
        }
        beforeCartAction(action);
      },
      after: (action: any) => {
        if (action.type === LOGOUT) finishLogout();
        else afterCartAction(action, false);
      },
      error: (action: any) => {
        if (action.type === LOGOUT) finishLogout();
        else afterCartAction(action, true);
      },
    }),
    watch(() => route.path, routeChanged),
    watch(
      () => cartLines().length,
      () => trackCheckoutStarted()
    )
  );
  window.addEventListener("vf:consent-updated", consentChanged);
  identifyCurrentCustomer();
  routeChanged();
});

onBeforeUnmount(() => {
  stops.splice(0).forEach((stop) => stop());
  window.removeEventListener("vf:consent-updated", consentChanged);
});
</script>
